import {
  ApiErrorCallback,
  ApiSuccessCallback,
  ApiSuccessCallbackParameterless,
} from './callback';
import {
  EmployeeRoles,
  Role,
  RoleCode,
} from '~/common/entities/Roles/RoleTypes';
import { Employee } from '~/common/entities/Department/DepartmentTypes';
import { useAuthenticationService } from './AuthenticationService';
import { authenticatedApi } from '../utils/authenticatedApi';
import { Nothing } from '~/common/entities/store/types';
import { createContainer } from 'unstated-next';

function useRole() {
  const { authenticationState } = useAuthenticationService();
  const getRoles = async (
    onSuccess?: ApiSuccessCallback<Role[]>,
    onError?: ApiErrorCallback,
  ) => {
    const { data, error } =
      await authenticatedApi(authenticationState).get<Role[]>('/roles/');
    if (error) return onError ? onError(error) : undefined;
    if (onSuccess) onSuccess(data);
  };

  const getRolesOfEmployee = async (
    employee: Employee,
    onSuccess?: ApiSuccessCallback<EmployeeRoles[]>,
    onError?: ApiErrorCallback,
  ) => {
    const { data, error } = await authenticatedApi(authenticationState).get<
      EmployeeRoles[]
    >(`/roles/${employee.id}/`);
    if (error) return onError ? onError(error) : undefined;
    if (onSuccess) onSuccess(data);
  };

  const addRoleToEmployee = async (
    employee: Employee,
    roleCode: RoleCode,
    onSuccess?: ApiSuccessCallback<EmployeeRoles[]>,
    onError?: ApiErrorCallback,
  ) => {
    const { data, error } = await authenticatedApi(authenticationState).post<
      EmployeeRoles[],
      Nothing
    >(`/roles/${employee.id}/${roleCode}/`, undefined);
    if (!error && onSuccess) onSuccess(data);
    if (error && onError) onError();
  };

  const removeRoleFromEmployee = async (
    employee: Employee,
    roleCode: RoleCode,
    onSuccess?: ApiSuccessCallbackParameterless,
    onError?: ApiErrorCallback,
  ) => {
    const { error } = await authenticatedApi(
      authenticationState,
    ).delete<Nothing>(`/roles/${employee.id}/${roleCode}/`);
    if (!error && onSuccess) onSuccess();
    if (error && onError) onError();
  };

  return {
    getRoles,
    getRolesOfEmployee,
    addRoleToEmployee,
    removeRoleFromEmployee,
  };
}

export const { Provider: RoleProvider, useContainer: useRoleService } =
  createContainer(useRole);
